import { AlertsIcon } from "@minket/brand/icons/alerts";
import { Button } from "@minket/ui/components/button";
import { StatusBadge } from "@minket/ui/components/status-badge";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@minket/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useState } from "react";
import { type Alert, AlertLedger } from "@/components/ops/alerts";
import { kindLabel } from "@/components/ops/format";
import type { RuleChange } from "@/components/ops/rule-editor.ondemand";
import { channelWords, opensWhen, type Rule, ruleGroups } from "@/components/ops/rules";
import { Choice } from "@/components/ops/select";
import { GhostTable } from "@/components/page/ghost";
import { Page } from "@/components/page/page";
import { OrgScope } from "@/components/page/scope";
import { Section, Sheet } from "@/components/page/section";
import { Notice, QueryNotice } from "@/components/page/states";
import { describeError } from "@/lib/api";
import { endpoints } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiKey, apiQuery, apiRequest } from "@/lib/query";
import { notify } from "@/lib/toast";

export const Route = createFileRoute("/alerts")({
  component: Alerts,
});

const loadRuleEditor = () => import("@/components/ops/rule-editor.ondemand");
const RuleEditor = lazy(() => loadRuleEditor().then((m) => ({ default: m.RuleEditor })));

const historyColumns = ["Severity", "Alert", "Opened, UTC"];
const ruleColumns = ["Alert", "Opens when", "Sent to", "State"];

function Alerts() {
  const manager = useCan("alerts.manage");
  return (
    <Page title="Alerts">
      <OrgScope
        will="Its alerts appear here as they open, with the rules that open them."
        icon={<AlertsIcon />}
        preview={() => <GhostTable columns={historyColumns} label="alerts" />}
      >
        {(org) => (
          <Sheet>
            <History orgId={org.organization_id} manager={manager} />
            <Rules orgId={org.organization_id} manager={manager} />
          </Sheet>
        )}
      </OrgScope>
    </Page>
  );
}

type Filter = "active" | "resolved" | "all";

const filters = {
  active: { title: "Open", empty: "No alert is open." },
  resolved: { title: "Resolved", empty: "No alert has been resolved yet." },
  all: { title: "Every alert", empty: "No alert has opened yet." },
} as const;

function History({ orgId, manager }: { orgId: string; manager: boolean }) {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<Filter>("active");
  const [cursor, setCursor] = useState<string[]>([]);
  const after = cursor.at(-1);
  const query = useQuery(
    apiQuery(
      endpoints.alerts,
      { orgId },
      { limit: 25, ...(filter === "all" ? {} : { status: filter }), ...(after ? { after } : {}) },
    ),
  );
  const update = useMutation({
    mutationFn: (input: { alert: Alert; status: "acknowledged" | "resolved" }) =>
      apiRequest(
        endpoints.updateAlert,
        { orgId, alertId: input.alert.id },
        { body: { status: input.status } },
      ),
    onSuccess: (alert) => {
      void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.alerts, { orgId }) });
      notify("success", alert.status === "resolved" ? "Alert resolved." : "Alert acknowledged.");
    },
  });
  const actions = (alert: Alert) => (
    <span className="inline-flex gap-1">
      {alert.status === "open" ? (
        <Button
          variant="ghost"
          size="sm"
          loading={
            update.isPending &&
            update.variables?.alert.id === alert.id &&
            update.variables.status === "acknowledged"
          }
          onClick={() => update.mutate({ alert, status: "acknowledged" })}
        >
          Acknowledge
          <span className="sr-only"> {alert.message}</span>
        </Button>
      ) : null}
      {alert.status !== "resolved" ? (
        <Button
          variant="ghost"
          size="sm"
          loading={
            update.isPending &&
            update.variables?.alert.id === alert.id &&
            update.variables.status === "resolved"
          }
          onClick={() => update.mutate({ alert, status: "resolved" })}
        >
          Resolve
          <span className="sr-only"> {alert.message}</span>
        </Button>
      ) : null}
    </span>
  );
  return (
    <Section
      wide
      title={filters[filter].title}
      action={
        <Choice
          label="Show"
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setCursor([]);
          }}
          options={[
            ["active", "Open and acknowledged"],
            ["resolved", "Resolved"],
            ["all", "Every alert"],
          ]}
        />
      }
    >
      <QueryNotice query={query} noun="alerts">
        {(data) =>
          data.data.length === 0 ? (
            <Notice tone={filter === "active" ? "success" : "neutral"}>
              {filters[filter].empty}
            </Notice>
          ) : (
            <div className="grid min-w-0 gap-3">
              <AlertLedger
                alerts={data.data}
                label="Alerts, newest first"
                actions={manager ? actions : undefined}
              />
              {update.isError ? (
                <p role="alert" className="text-sm text-destructive wrap-break-word">
                  {describeError(update.error).message}
                </p>
              ) : null}
              {cursor.length > 0 || data.has_more ? (
                <nav aria-label="Alert pages" className="flex flex-wrap gap-2">
                  {cursor.length > 0 ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCursor(cursor.slice(0, -1))}
                    >
                      Newer alerts
                    </Button>
                  ) : null}
                  {data.has_more ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const last = data.data.at(-1);
                        if (last) setCursor([...cursor, last.id]);
                      }}
                    >
                      Older alerts
                    </Button>
                  ) : null}
                </nav>
              ) : null}
            </div>
          )
        }
      </QueryNotice>
      {query.isSuccess ? null : (
        <GhostTable
          columns={historyColumns}
          label="alerts"
          rows={3}
          widths={["8rem", undefined, "9rem"]}
        />
      )}
    </Section>
  );
}

function Rules({ orgId, manager }: { orgId: string; manager: boolean }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Rule | null>(null);
  const query = useQuery(apiQuery(endpoints.alertRules, { orgId }));
  const save = useMutation({
    mutationFn: (change: RuleChange) =>
      apiRequest(endpoints.updateAlertRule, { orgId, ruleId: editing?.id ?? "" }, { body: change }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.alertRules, { orgId }) });
      setEditing(null);
      notify("success", "Rule saved.");
    },
  });
  return (
    <Section wide title="Rules">
      <QueryNotice query={query} noun="alert rules">
        {(data) => {
          const rules = data.data.filter((rule) => rule.project_id === null);
          return ruleGroups.map((group) => {
            const kinds: readonly string[] = group.kinds;
            const listed = rules
              .filter((rule) => kinds.includes(rule.kind))
              .sort((a, b) => kinds.indexOf(a.kind) - kinds.indexOf(b.kind));
            if (listed.length === 0) return null;
            return (
              <Table key={group.title} className="text-[13.5px]">
                <TableCaption className="mt-0 mb-2 text-left text-sm font-semibold text-foreground [caption-side:top]">
                  {group.title}
                  <span className="sr-only"> rules</span>
                </TableCaption>
                <TableHeader>
                  <TableRow>
                    {ruleColumns.map((column) => (
                      <TableHead key={column}>{column}</TableHead>
                    ))}
                    {manager ? (
                      <TableHead className="w-0">
                        <span className="sr-only">Edit</span>
                      </TableHead>
                    ) : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {listed.map((rule) => {
                    const channels = channelWords(rule.channels);
                    return (
                      <TableRow key={rule.id} className="hover:bg-transparent">
                        <TableCell className="font-semibold whitespace-nowrap">
                          {kindLabel(rule.kind)}
                        </TableCell>
                        <TableCell className="min-w-56 text-muted-foreground">
                          {opensWhen(rule)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {channels.length === 0 ? (
                            <span className="text-muted-foreground">The console only</span>
                          ) : (
                            channels.join(", ")
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <StatusBadge tone={rule.enabled ? "success" : "neutral"}>
                            {rule.enabled ? "On" : "Off"}
                          </StatusBadge>
                        </TableCell>
                        {manager ? (
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Edit the ${kindLabel(rule.kind).toLowerCase()} rule`}
                              onPointerEnter={() => void loadRuleEditor()}
                              onClick={() => {
                                save.reset();
                                setEditing(rule);
                              }}
                            >
                              Edit
                            </Button>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            );
          });
        }}
      </QueryNotice>
      {query.isSuccess ? null : <GhostTable columns={ruleColumns} label="alert rules" rows={3} />}
      {editing ? (
        <Suspense fallback={null}>
          <RuleEditor rule={editing} save={save} onClose={() => setEditing(null)} />
        </Suspense>
      ) : null}
    </Section>
  );
}
